// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { PaginationStyle } from "@insite/mobius/Pagination";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@werner/App/Components/Common/XngZone";
import { CheckAndHideEmptyDiv } from "@werner/XDL/constants";
import { xScreen } from "@werner/XDL/Utilities";
import React, { FC } from "react";
import styled, { css } from "styled-components";

export interface MyAccountHistoryPagesLayoutStyles {
    container?: InjectableCss;
    titleContainer?: InjectableCss;
    extraWidgetContainer?: InjectableCss;
    filterResultContainer?: InjectableCss;
    tagsContainer?: InjectableCss;
    topPaginationContainer?: InjectableCss;
    tableContainer?: InjectableCss;
    tableHeaderContainer?: InjectableCss;
    bottomPaginationContainer?: InjectableCss;
}

export const myAccountHistoryPagesLayoutStyles: MyAccountHistoryPagesLayoutStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    extraWidgetContainer: {
        css: css`
            ${CheckAndHideEmptyDiv}
        `,
    },
    filterResultContainer: {
        css: css`
            ${CheckAndHideEmptyDiv}
            display: flex;
            justify-content: space-between;
        `,
    },
    tagsContainer: {
        css: css`
            ${CheckAndHideEmptyDiv}
        `,
    },
    topPaginationContainer: {
        css: css`
            ${CheckAndHideEmptyDiv}
        `,
    },
    tableContainer: {
        css: css`
            ${CheckAndHideEmptyDiv}
            margin-top: 8px;
            display: flex;
            flex-direction: column;
        `,
    },
    tableHeaderContainer: {
        css: css`
            ${CheckAndHideEmptyDiv}
            display: flex;
            justify-content: space-between;
            align-items: center;
            ${xScreen("lte", "md")} {
                flex-wrap: wrap;
            }
            & > div:last-child {
                display: flex;
                justify-content: flex-end;
                ${PaginationStyle} {
                    gap: 12px;
                }
            }
        `,
    },
    bottomPaginationContainer: {
        css: css`
            ${CheckAndHideEmptyDiv}
            margin-top: 8px;
            & > div {
                display: flex;
                justify-content: flex-end;
            }
        `,
    },
};

const styles = myAccountHistoryPagesLayoutStyles;
const MyAccountDetailsPagesLayout: FC<WidgetProps> = ({ id }) => {
    return (
        <StyledWrapper {...styles.container} data-test-selector="My-Account-History-Pages-Layout">
            <StyledWrapper {...styles.titleContainer}>
                <XngZone contentId={id} zoneName="TitleZone" hint="Zone For Title" />
            </StyledWrapper>
            <StyledWrapper {...styles.filterResultContainer}>
                <XngZone contentId={id} zoneName="FiltersZone" hint="Zone For Filter Container" />
            </StyledWrapper>
            <StyledWrapper {...styles.tagsContainer}>
                <XngZone contentId={id} zoneName="TagsZone" hint="Zone For Tags" />
            </StyledWrapper>
            <StyledWrapper {...styles.extraWidgetContainer}>
                <XngZone contentId={id} zoneName="ExtraWidgetsZone" hint="Zone For Extra Widgets" />
            </StyledWrapper>
            <StyledWrapper {...styles.tableContainer}>
                <StyledWrapper {...styles.tableHeaderContainer}>
                    <XngZone contentId={id} zoneName="ResultsCount" hint="Zone For Results Count" />
                    <XngZone contentId={id} zoneName="TopPaginationZone" hint="Zone For Top Pagination" />
                </StyledWrapper>
                <XngZone contentId={id} zoneName="TableZone" hint="Zone For Table" />
            </StyledWrapper>
            <StyledWrapper {...styles.bottomPaginationContainer}>
                <XngZone contentId={id} zoneName="BottomPaginationZone" hint="Zone For Bottom Pagination" />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: MyAccountDetailsPagesLayout,
    definition: {
        group: "Layout" as any,
        displayName: "My Account History Pages Layout",
        allowedContexts: [
            "OrderHistoryPage",
            "VmiOrderHistoryPage",
            "SavedOrderListPage",
            "OrderApprovalListPage",
            "RfqMyQuotesPage",
            "InvoiceHistoryPage",
        ],
    },
};

export default widgetModule;
